import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import {
    locale,
    getNumberFormat,
    getSliderNumberFormatConfig,
} from '@trqk/utils';

import { currentPlanPadding } from '../../constants';
import { getPaymentNumberFormat } from '../../utils';
import { HASHES, PATHS } from '../../../../../../../constants';

interface CurrentPlanAmountsProps {
    children?: ReactNode;
    /**
     * Label for the payment setting
     */
    label: string;
    /**
     * Type for the payment setting - see {@link PaymentSettingType}
     */
    type: PaymentSettingType;
    /**
     * When true, the `usageDetail` is ignored and the word "Unlimited" is used for the Available
     * amount label.
     */
    unlimited?: boolean;
    /**
     * Current usage details for this payment setting
     */
    usageDetail: PaymentPlanUsageDetail;
}

/**
 * Displays the current amount used and amount available. Amounts are not adjustable.
 * @param props - see {@link CurrentPlanAmountsProps}
 */
export const CurrentPlanAmounts = ({
    children,
    label,
    type,
    unlimited = false,
    usageDetail,
}: CurrentPlanAmountsProps): JSX.Element => {
    let usedString = locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.NONE;
    let availableString = locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.NONE;
    const numberFormat = getPaymentNumberFormat(type);
    const { capacity, exceedsCapacity, used } = usageDetail;

    if (used !== null) {
        const { value: valueFormatOptions } =
            getSliderNumberFormatConfig(numberFormat);
        const valueFormatter = getNumberFormat('en', valueFormatOptions);

        usedString = valueFormatter.format(used);
    }

    if (unlimited) {
        availableString = locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.UNLIMITED;
    } else if (typeof capacity === 'number' && capacity > 0) {
        const { value: valueFormatOptions } =
            getSliderNumberFormatConfig(numberFormat);
        const valueFormatter = getNumberFormat('en', valueFormatOptions);

        availableString = valueFormatter.format(capacity);
    }

    const showUsed = usageDetail.used !== null;

    return (
        <div
            className="rounded-md bg-tertiary-100"
            style={{
                padding: currentPlanPadding,
            }}
        >
            <div className="text-sm font-semibold mb-4">{label}</div>
            <div className="space-y-4">
                {showUsed && (
                    <div>
                        <div className="rounded-md bg-white p-2 flex justify-between items-baseline">
                            <div className="text-sm font-semibold text-tertiary-400">
                                {locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.USED}
                            </div>
                            <div
                                className={cx('font-bold', {
                                    'text-primary-500': !exceedsCapacity,
                                    'text-highlight-magenta': exceedsCapacity,
                                })}
                            >
                                {usedString}
                            </div>
                        </div>
                        {type === 'QUARTERLY_ROYALTIES' && (
                            <div className="w-full text-right mt-1">
                                <Link
                                    className="text-sm underline text-primary-500"
                                    to={({ search }) => ({
                                        pathname: PATHS.REPORTS,
                                        hash: HASHES.REPORTS.TOTAL_ROYALTIES,
                                        search,
                                    })}
                                >
                                    {
                                        locale.enUS.ACCOUNT.SECTIONS
                                            .PAYMENT_PLAN.VIEW_CALCULATION
                                    }
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                <div className="rounded-md bg-white p-2 flex justify-between items-baseline">
                    <div className="text-sm font-semibold text-tertiary-400">
                        {locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.AVAILABLE}
                    </div>
                    <div>{availableString}</div>
                </div>
            </div>
            {children}
        </div>
    );
};
